import { useState, useMemo, useCallback } from 'react';
import { useInfiniteQuery, InfiniteData } from '@tanstack/react-query';
import { api, LeadsPageResponse, GetLeadsPageParams } from '@/services/api';
import { Lead } from '@/types';
import { queryClient } from './queryClient';

export interface UseLeadsQueryOptions {
  userId?: string | null;
  lojaId?: string | null;
  tenantId?: string | null;
  etapaId?: string | null;
  status?: string | null;
  responsavelUserId?: string | null;
  q?: string;
  signal?: AbortSignal;
}

export function buildLeadsQueryKey(options?: UseLeadsQueryOptions) {
  return [
    'leads',
    {
      userId: options?.userId || null,
      lojaId: options?.lojaId || null,
      tenantId: options?.tenantId || null,
      etapaId: options?.etapaId || null,
      status: options?.status || null,
      responsavelUserId: options?.responsavelUserId || null,
      q: options?.q || ''
    }
  ] as const;
}

// Comparador determinístico de timestamps descendente com microssegundos (ISO 8601, NULLS LAST)
export function compareLeadTimestampsDesc(dateA?: string | null, dateB?: string | null): number {
  if (!dateA && !dateB) return 0;
  if (!dateA) return 1; // NULLS LAST
  if (!dateB) return -1; // NULLS LAST

  const timeA = new Date(dateA).getTime();
  const timeB = new Date(dateB).getTime();

  if (isNaN(timeA) && isNaN(timeB)) {
    return String(dateB).localeCompare(String(dateA)); // DESC
  }
  if (isNaN(timeA)) return 1;
  if (isNaN(timeB)) return -1;

  if (timeA !== timeB) {
    return timeB - timeA; // DESC
  }

  // Desempate por fração de segundos (microssegundos/nanossegundos)
  const matchA = dateA.match(/\.(\d+)/);
  const matchB = dateB.match(/\.(\d+)/);

  if (matchA || matchB) {
    const fracA = (matchA ? matchA[1] : '').padEnd(9, '0').slice(0, 9);
    const fracB = (matchB ? matchB[1] : '').padEnd(9, '0').slice(0, 9);
    if (fracA !== fracB) {
      return fracB.localeCompare(fracA); // DESC
    }
  }

  return 0;
}

export function getLeadRawLastMessageAt(lead: Lead): string | null {
  if (lead && lead.last_message_at_raw !== undefined) {
    return lead.last_message_at_raw;
  }
  return lead?.last_message_at || null;
}

export function getLeadRawCreatedAt(lead: Lead): string | null {
  if (lead && lead.created_at_raw !== undefined) {
    return lead.created_at_raw;
  }
  return lead?.created_at || null;
}

// Comparador determinístico de leads usando a tupla do banco: last_message_at DESC NULLS LAST, created_at DESC NULLS LAST, id DESC
export function compareLeads(a: Lead, b: Lead): number {
  const rawMsgA = getLeadRawLastMessageAt(a);
  const rawMsgB = getLeadRawLastMessageAt(b);
  const timeDiffMsg = compareLeadTimestampsDesc(rawMsgA, rawMsgB);
  if (timeDiffMsg !== 0) {
    return timeDiffMsg;
  }
  const rawCreatedA = getLeadRawCreatedAt(a);
  const rawCreatedB = getLeadRawCreatedAt(b);
  const timeDiffCreated = compareLeadTimestampsDesc(rawCreatedA, rawCreatedB);
  if (timeDiffCreated !== 0) {
    return timeDiffCreated;
  }
  return (b.id || '').localeCompare(a.id || ''); // DESC tie-breaker por ID
}

export type LeadMutationType = 'insert' | 'update' | 'delete' | 'rollback_delete' | 'column_leave' | 'rollback_column_leave';

export interface LeadMutationRecord {
  id: string;
  type: LeadMutationType;
  seq: number;
  data?: any;
}

// ============================================================================
// Registro de Lifecycle, Geração e Revogação por Contexto
// ============================================================================
export interface LeadContextLifecycle {
  generation: number;
  isRevoked: boolean;
  status?: number;
  message?: string;
}

let globalLeadGenerationCounter = 1;
let globalLeadRecoveryCounter = 0;
const leadContextLifecycleMap = new Map<string, LeadContextLifecycle>();
const activeLeadRecoveryByKey = new Map<string, number>();

// Mutações e reconciliação temporal
const leadMutationSeqByKey = new Map<string, number>();
const leadMutationsByKey = new Map<string, Map<string, LeadMutationRecord>>();
const leadTombstonesByKey = new Map<string, Set<string>>();
const activeLeadFetchesByKey = new Map<string, Map<number, number>>();
let globalLeadFetchIdCounter = 0;

export function getLeadContextLifecycle(storageKey: string): LeadContextLifecycle {
  let ctx = leadContextLifecycleMap.get(storageKey);
  if (!ctx) {
    ctx = { generation: ++globalLeadGenerationCounter, isRevoked: false };
    leadContextLifecycleMap.set(storageKey, ctx);
  }
  return ctx;
}

export function isLeadContextRevoked(storageKey: string): boolean {
  const ctx = leadContextLifecycleMap.get(storageKey);
  return Boolean(ctx?.isRevoked);
}

export function isLeadOperationValid(storageKey: string, expectedGeneration?: number): boolean {
  const ctx = leadContextLifecycleMap.get(storageKey);
  if (!ctx) return false;
  if (ctx.isRevoked) return false;
  if (expectedGeneration !== undefined && ctx.generation !== expectedGeneration) return false;
  return true;
}

export function isLeadCallbackActive(storageKey: string, callbackGeneration: number): boolean {
  return isLeadOperationValid(storageKey, callbackGeneration);
}

export function revokeLeadContext(
  storageKey: string,
  status: number,
  message: string
): LeadContextLifecycle {
  let ctx = leadContextLifecycleMap.get(storageKey);
  if (!ctx) {
    ctx = { generation: ++globalLeadGenerationCounter, isRevoked: true, status, message };
    leadContextLifecycleMap.set(storageKey, ctx);
  } else {
    ctx.isRevoked = true;
    ctx.status = status;
    ctx.message = message;
    ctx.generation = ++globalLeadGenerationCounter;
  }

  clearLeadsTracking(storageKey);
  return ctx;
}

export function resetLeadContextAuthorization(storageKey: string): LeadContextLifecycle {
  const ctx: LeadContextLifecycle = {
    generation: ++globalLeadGenerationCounter,
    isRevoked: false,
    status: undefined,
    message: undefined
  };
  leadContextLifecycleMap.set(storageKey, ctx);
  return ctx;
}

export function clearLeadsTracking(storageKey?: string) {
  if (storageKey) {
    leadMutationsByKey.delete(storageKey);
    leadMutationSeqByKey.delete(storageKey);
    activeLeadFetchesByKey.delete(storageKey);
    leadTombstonesByKey.delete(storageKey);
    activeLeadRecoveryByKey.delete(storageKey);
  } else {
    for (const ctx of leadContextLifecycleMap.values()) {
      ctx.isRevoked = true;
      ctx.generation = ++globalLeadGenerationCounter;
    }
    leadMutationsByKey.clear();
    leadMutationSeqByKey.clear();
    activeLeadFetchesByKey.clear();
    leadTombstonesByKey.clear();
    activeLeadRecoveryByKey.clear();
    globalLeadGenerationCounter += 1000;
  }
}

export function clearLeadsCache(customQueryClient = queryClient) {
  for (const ctx of leadContextLifecycleMap.values()) {
    ctx.isRevoked = true;
    ctx.generation = ++globalLeadGenerationCounter;
  }
  leadMutationsByKey.clear();
  leadMutationSeqByKey.clear();
  activeLeadFetchesByKey.clear();
  leadTombstonesByKey.clear();
  activeLeadRecoveryByKey.clear();
  globalLeadGenerationCounter += 1000;
  customQueryClient.removeQueries({ queryKey: ['leads'] });
}

export function recordLeadMutation(
  storageKey: string,
  id: string,
  type: LeadMutationType,
  data?: any
): number {
  if (isLeadContextRevoked(storageKey)) {
    return -1;
  }

  const currentSeq = (leadMutationSeqByKey.get(storageKey) || 0) + 1;
  leadMutationSeqByKey.set(storageKey, currentSeq);

  let map = leadMutationsByKey.get(storageKey);
  if (!map) {
    map = new Map<string, LeadMutationRecord>();
    leadMutationsByKey.set(storageKey, map);
  }

  let tombstones = leadTombstonesByKey.get(storageKey);
  if (!tombstones) {
    tombstones = new Set<string>();
    leadTombstonesByKey.set(storageKey, tombstones);
  }

  if (type === 'delete') {
    // Exclusão confirmada: tombstone permanente
    tombstones.add(id);
    map.set(id, { id, type: 'delete', seq: currentSeq });
  } else if (type === 'column_leave') {
    // Mudança de etapa/coluna: SEM tombstone (permite retornar à coluna no futuro)
    map.set(id, { id, type: 'column_leave', seq: currentSeq });
  } else if (type === 'rollback_delete') {
    tombstones.delete(id);
    if (data) {
      map.set(id, { id, type: 'insert', seq: currentSeq, data });
    } else {
      map.delete(id);
    }
  } else if (type === 'rollback_column_leave') {
    if (data) {
      map.set(id, { id, type: 'insert', seq: currentSeq, data });
    } else {
      map.delete(id);
    }
  } else if (type === 'update') {
    if (tombstones.has(id)) {
      return currentSeq;
    }
    const existing = map.get(id);
    if (existing && existing.type === 'update') {
      map.set(id, { id, type: 'update', seq: currentSeq, data: { ...existing.data, ...data } });
    } else {
      map.set(id, { id, type: 'update', seq: currentSeq, data });
    }
  } else if (type === 'insert') {
    if (tombstones.has(id)) {
      return currentSeq;
    }
    map.set(id, { id, type: 'insert', seq: currentSeq, data });
  }

  return currentSeq;
}

export function startLeadFetchTracking(storageKey: string): { fetchId: number; startSeq: number } {
  const fetchId = ++globalLeadFetchIdCounter;
  const startSeq = leadMutationSeqByKey.get(storageKey) || 0;

  let activeMap = activeLeadFetchesByKey.get(storageKey);
  if (!activeMap) {
    activeMap = new Map<number, number>();
    activeLeadFetchesByKey.set(storageKey, activeMap);
  }
  activeMap.set(fetchId, startSeq);

  return { fetchId, startSeq };
}

export function finishLeadFetchTracking(storageKey: string, fetchId: number, startSeq: number) {
  const activeMap = activeLeadFetchesByKey.get(storageKey);
  if (activeMap) {
    activeMap.delete(fetchId);
    if (activeMap.size === 0) {
      activeLeadFetchesByKey.delete(storageKey);
      const map = leadMutationsByKey.get(storageKey);
      if (map) {
        for (const [id, mut] of Array.from(map.entries())) {
          if (mut.seq <= startSeq && mut.type !== 'delete') {
            map.delete(id);
          }
        }
      }
    }
  }
}

export function reconcileFetchedLeads(
  storageKey: string,
  fetchedItems: Lead[],
  startSeq: number
): Lead[] {
  const map = leadMutationsByKey.get(storageKey);
  const tombstones = leadTombstonesByKey.get(storageKey);

  const resultItems: Lead[] = [];
  const handledIds = new Set<string>();

  for (const item of fetchedItems) {
    if (!item || !item.id) continue;
    if (tombstones && tombstones.has(item.id)) continue;

    if (map) {
      const mut = map.get(item.id);
      if (mut) {
        if (mut.type === 'delete' || mut.type === 'column_leave') continue;
        // D3: Reconciliação temporal — mutação local só sobrevive se for posterior ao início do fetch
        if (mut.type === 'update' && mut.seq > startSeq) {
          resultItems.push({ ...item, ...mut.data });
          handledIds.add(item.id);
          continue;
        }
      }
    }

    resultItems.push(item);
    handledIds.add(item.id);
  }

  // Inclui inserts locais registrados durante o fetch
  if (map) {
    for (const mut of map.values()) {
      if (mut.type === 'insert' && mut.seq > startSeq && !handledIds.has(mut.id) && mut.data) {
        if (tombstones && tombstones.has(mut.id)) continue;
        resultItems.push(mut.data);
        handledIds.add(mut.id);
      }
    }
  }

  return resultItems;
}

// Consolidador das páginas de Leads do TanStack Query + mutações locais ativas
export function getConsolidatedLeads(
  storageKey: string,
  pages?: { items: Lead[] }[]
): Lead[] {
  if (isLeadContextRevoked(storageKey)) return [];

  const map = leadMutationsByKey.get(storageKey);
  const tombstones = leadTombstonesByKey.get(storageKey);
  const activeMutations = map ? new Map(map) : null;
  const leadMap = new Map<string, Lead>();

  if (pages) {
    for (const page of pages) {
      if (page?.items) {
        for (const lead of page.items) {
          if (!lead || !lead.id) continue;
          if (tombstones && tombstones.has(lead.id)) continue;
          if (activeMutations) {
            const mut = activeMutations.get(lead.id);
            if (mut) {
              if (mut.type === 'delete' || mut.type === 'column_leave') continue;
              if (mut.type === 'update') {
                leadMap.set(lead.id, { ...lead, ...mut.data });
                continue;
              }
            }
          }
          leadMap.set(lead.id, lead);
        }
      }
    }
  }

  if (activeMutations) {
    for (const mut of activeMutations.values()) {
      if (mut.type === 'insert' && mut.data && !leadMap.has(mut.id)) {
        if (tombstones && tombstones.has(mut.id)) continue;
        leadMap.set(mut.id, mut.data);
      }
    }
  }

  const list = Array.from(leadMap.values());
  list.sort(compareLeads);
  return list;
}

function getLeadStorageKey(key: readonly any[]): string {
  return JSON.stringify(key);
}

/**
 * Recuperação autoritativa de Leads: cobre do topo até a fronteira histórica previamente carregada.
 * NUNCA encolhe a lista em refreshes; aborta sem publicar resultado parcial em caso de erro intermediário.
 */
export async function recoverLeads(
  options?: UseLeadsQueryOptions,
  customQueryClient = queryClient,
  callerGeneration?: number
): Promise<InfiniteData<LeadsPageResponse> | undefined> {
  const queryKey = buildLeadsQueryKey(options);
  const storageKey = getLeadStorageKey(queryKey);

  const initialCtx = getLeadContextLifecycle(storageKey);
  const opGen = callerGeneration ?? initialCtx.generation;

  if (initialCtx.isRevoked || !isLeadOperationValid(storageKey, opGen)) {
    return undefined;
  }

  const recoveryId = ++globalLeadRecoveryCounter;
  activeLeadRecoveryByKey.set(storageKey, recoveryId);

  const currentCached = customQueryClient.getQueryData<InfiniteData<LeadsPageResponse>>(queryKey);
  let oldestKnownLead: Lead | null = null;
  let totalCachedCount = 0;

  if (currentCached?.pages && currentCached.pages.length > 0) {
    for (const page of currentCached.pages) {
      if (page?.items && page.items.length > 0) {
        totalCachedCount += page.items.length;
        for (const item of page.items) {
          if (item?.id) {
            if (!oldestKnownLead || compareLeads(item, oldestKnownLead) > 0) {
              oldestKnownLead = item;
            }
          }
        }
      }
    }
  }

  const oldestKnownId = oldestKnownLead?.id || null;

  const { fetchId, startSeq } = startLeadFetchTracking(storageKey);
  const recoveredPages: LeadsPageResponse[] = [];
  const recoveredPageParams: (string | null)[] = [null];
  let nextCursor: string | null = null;
  let hasMore = true;
  let coveredOldest = false;

  // D6: Se não havia itens prévios em cache (primeira carga), busca estritamente 1 página (50 itens)!
  const isInitialLoad = totalCachedCount === 0;

  try {
    while (hasMore) {
      if (!isLeadOperationValid(storageKey, opGen) || activeLeadRecoveryByKey.get(storageKey) !== recoveryId) {
        finishLeadFetchTracking(storageKey, fetchId, startSeq);
        return undefined;
      }

      const pageRes = await api.getLeadsPage({
        cursor: nextCursor,
        limit: 50,
        loja_id: options?.lojaId,
        tenant_id: options?.tenantId,
        etapa_id: options?.etapaId,
        status: options?.status,
        responsavel_user_id: options?.responsavelUserId,
        q: options?.q,
        signal: options?.signal
      });

      if (!isLeadOperationValid(storageKey, opGen) || activeLeadRecoveryByKey.get(storageKey) !== recoveryId) {
        finishLeadFetchTracking(storageKey, fetchId, startSeq);
        return undefined;
      }

      recoveredPages.push(pageRes);

      // Carga inicial: encerra após a primeira página
      if (isInitialLoad) {
        hasMore = false;
        break;
      }

      // Verificação 1: o ID mais antigo conhecido foi coberto diretamente
      if (oldestKnownId && pageRes.items?.some(l => l.id === oldestKnownId)) {
        coveredOldest = true;
      }

      // Verificação 2: se o ID mais antigo foi removido no servidor, ordenação temporal confirma que passamos por ele
      if (oldestKnownLead && !coveredOldest && pageRes.items && pageRes.items.length > 0) {
        const lastInPage = pageRes.items[pageRes.items.length - 1];
        if (compareLeads(lastInPage, oldestKnownLead) >= 0) {
          coveredOldest = true;
        }
      }

      if (coveredOldest || !pageRes.hasMore || !pageRes.nextCursor) {
        hasMore = false;
        break;
      }

      recoveredPageParams.push(pageRes.nextCursor);
      nextCursor = pageRes.nextCursor;
    }

    if (!isLeadOperationValid(storageKey, opGen) || activeLeadRecoveryByKey.get(storageKey) !== recoveryId) {
      finishLeadFetchTracking(storageKey, fetchId, startSeq);
      return undefined;
    }

    // Salvaguarda: se havia itens em cache e a recuperação parou antes de cobrir a fronteira com hasMore=true, rejeita
    if (totalCachedCount > 0 && !coveredOldest && hasMore) {
      finishLeadFetchTracking(storageKey, fetchId, startSeq);
      return undefined;
    }

    // Reconcilia mutações locais registradas durante o fetch em todas as páginas
    for (let i = 0; i < recoveredPages.length; i++) {
      recoveredPages[i] = {
        ...recoveredPages[i],
        items: reconcileFetchedLeads(storageKey, recoveredPages[i].items, startSeq)
      };
    }

    const finalData: InfiniteData<LeadsPageResponse> = {
      pages: recoveredPages,
      pageParams: recoveredPageParams
    };

    customQueryClient.setQueryData<InfiniteData<LeadsPageResponse>>(queryKey, finalData);
    finishLeadFetchTracking(storageKey, fetchId, startSeq);
    return finalData;
  } catch (err) {
    finishLeadFetchTracking(storageKey, fetchId, startSeq);
    throw err;
  }
}

/**
 * Hook TanStack Query v5 para listagem de Leads com paginação por cursor,
 * revalidação autoritativa contínua e reconciliação em tempo real.
 */
export function useLeadsQuery(options?: UseLeadsQueryOptions) {
  const queryKey = useMemo(() => buildLeadsQueryKey(options), [
    options?.userId,
    options?.lojaId,
    options?.tenantId,
    options?.etapaId,
    options?.status,
    options?.responsavelUserId,
    options?.q
  ]);
  const storageKey = useMemo(() => getLeadStorageKey(queryKey), [queryKey]);

  const currentLifecycle = getLeadContextLifecycle(storageKey);
  const myGeneration = currentLifecycle.generation;

  const [syncError, setSyncError] = useState<Error | null>(null);
  const [accessDeniedError, setAccessDeniedError] = useState<{ status: number; message: string } | null>(null);
  const [, setMutationTick] = useState(0);

  const isRevoked = isLeadContextRevoked(storageKey);
  const effectiveAccessDenied = useMemo(() => {
    if (accessDeniedError) return accessDeniedError;
    if (isRevoked) {
      return {
        status: currentLifecycle.status || 403,
        message: currentLifecycle.message || (currentLifecycle.status === 401 ? 'Não autorizado' : 'Acesso negado')
      };
    }
    return null;
  }, [accessDeniedError, isRevoked, currentLifecycle.status, currentLifecycle.message]);

  const query = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam, signal }) => {
      if (!isLeadOperationValid(storageKey, myGeneration) || effectiveAccessDenied) {
        return { items: [], nextCursor: null, hasMore: false, totalCount: 0 };
      }
      try {
        return await api.getLeadsPage({
          cursor: pageParam as string | null,
          limit: 50,
          loja_id: options?.lojaId,
          tenant_id: options?.tenantId,
          etapa_id: options?.etapaId,
          status: options?.status,
          responsavel_user_id: options?.responsavelUserId,
          q: options?.q,
          signal: signal || options?.signal
        });
      } catch (err: any) {
        if (err?.status === 401 || err?.status === 403) {
          setAccessDeniedError({ status: err.status, message: err.message });
          revokeLeadContext(storageKey, err.status, err.message);
          queryClient.setQueryData(queryKey, { pages: [], pageParams: [] });
        }
        throw err;
      }
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage?.nextCursor || undefined,
    enabled: !isLeadContextRevoked(storageKey) && !effectiveAccessDenied,
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) return false;
      return failureCount < 2;
    }
  });

  const leads = useMemo(() => {
    if (effectiveAccessDenied || isRevoked) return [];
    return getConsolidatedLeads(storageKey, query.data?.pages);
  }, [storageKey, query.data, effectiveAccessDenied, isRevoked]);

  // Callback de refetch referencialmente estável
  const refetch = useCallback(async () => {
    if (!isLeadCallbackActive(storageKey, myGeneration)) {
      return { data: undefined, isError: false, error: null };
    }
    try {
      const recovered = await recoverLeads(
        {
          userId: options?.userId,
          lojaId: options?.lojaId,
          tenantId: options?.tenantId,
          etapaId: options?.etapaId,
          status: options?.status,
          responsavelUserId: options?.responsavelUserId,
          q: options?.q
        },
        queryClient,
        myGeneration
      );
      if (isLeadCallbackActive(storageKey, myGeneration)) {
        setSyncError(null);
      }
      return { data: recovered, isError: false, error: null };
    } catch (err: any) {
      if (err?.status === 401 || err?.status === 403) {
        setAccessDeniedError({ status: err.status, message: err.message });
        revokeLeadContext(storageKey, err.status, err.message);
        queryClient.setQueryData(queryKey, { pages: [], pageParams: [] });
        return { data: undefined, isError: true, error: err };
      }
      if (isLeadCallbackActive(storageKey, myGeneration)) {
        setSyncError(err);
      }
      return { data: undefined, isError: true, error: err };
    }
  }, [
    storageKey,
    myGeneration,
    queryKey,
    options?.userId,
    options?.lojaId,
    options?.tenantId,
    options?.etapaId,
    options?.status,
    options?.responsavelUserId,
    options?.q
  ]);

  const loadMore = useCallback(async () => {
    if (!isLeadCallbackActive(storageKey, myGeneration)) return;
    if (query.hasNextPage && !query.isFetchingNextPage) {
      await query.fetchNextPage();
    }
  }, [storageKey, myGeneration, query.hasNextPage, query.isFetchingNextPage, query.fetchNextPage]);

  const addRealtimeLead = useCallback((lead: Lead) => {
    if (!isLeadCallbackActive(storageKey, myGeneration)) return;
    const currentTombstones = leadTombstonesByKey.get(storageKey);
    if (currentTombstones && currentTombstones.has(lead.id)) return;

    recordLeadMutation(storageKey, lead.id, 'insert', lead);
    queryClient.setQueryData<InfiniteData<LeadsPageResponse>>(queryKey, (old) => {
      if (!old || !old.pages.length) {
        return {
          pages: [{ items: [lead], nextCursor: null, hasMore: false, totalCount: 1 }],
          pageParams: [null]
        };
      }
      const exists = old.pages.some(p => p.items.some(l => l.id === lead.id));
      if (exists) {
        return {
          ...old,
          pages: old.pages.map(p => ({
            ...p,
            items: p.items.map(l => l.id === lead.id ? { ...l, ...lead } : l)
          }))
        };
      }
      const newPages = [...old.pages];
      newPages[0] = {
        ...newPages[0],
        items: [lead, ...newPages[0].items]
      };
      return {
        ...old,
        pages: newPages
      };
    });
    setMutationTick(t => t + 1);
  }, [storageKey, myGeneration, queryKey]);

  const updateLeadInCache = useCallback((id: string, updates: Partial<Lead>) => {
    if (!isLeadCallbackActive(storageKey, myGeneration)) return;
    const currentTombstones = leadTombstonesByKey.get(storageKey);
    if (currentTombstones && currentTombstones.has(id)) return;

    recordLeadMutation(storageKey, id, 'update', updates);
    queryClient.setQueryData<InfiniteData<LeadsPageResponse>>(queryKey, (old) => {
      if (!old) return old;
      return {
        ...old,
        pages: old.pages.map(p => ({
          ...p,
          items: p.items.map(l => l.id === id ? { ...l, ...updates } : l)
        }))
      };
    });
    setMutationTick(t => t + 1);
  }, [storageKey, myGeneration, queryKey]);

  const removeLeadFromCache = useCallback((id: string) => {
    if (!isLeadCallbackActive(storageKey, myGeneration)) return;
    recordLeadMutation(storageKey, id, 'delete');
    queryClient.setQueryData<InfiniteData<LeadsPageResponse>>(queryKey, (old) => {
      if (!old) return old;
      return {
        ...old,
        pages: old.pages.map(p => ({
          ...p,
          items: p.items.filter(l => l.id !== id)
        }))
      };
    });
    setMutationTick(t => t + 1);
  }, [storageKey, myGeneration, queryKey]);

  const isError = Boolean(query.isError || syncError || effectiveAccessDenied);
  const error = syncError || (effectiveAccessDenied ? new Error(effectiveAccessDenied.message) : null) || query.error || null;

  return {
    leads,
    totalCount: query.data?.pages?.[0]?.totalCount ?? leads.length,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: Boolean(query.hasNextPage),
    isError,
    error,
    syncError,
    refetch,
    loadMore,
    addRealtimeLead,
    updateLeadInCache,
    removeLeadFromCache
  };
}
